import React, { useCallback, useState } from 'react'

import { useGoogleLogin, GoogleOAuthProvider } from '@react-oauth/google'
import { useRouter } from 'solito/router'

import { useSendGoogleAnalyticsEvent } from 'app/hooks/useSendGoogleAnalyticsEvent'
import ErrorHandler, { isErrorHandlerType } from 'app/services/ErrorHandler'
import {
  useGoogleLoginMutation,
  useGoogleRegisterMutation
} from 'app/services/Query/user/user.mutation'
import useAuthStore, { getAuthActions } from 'app/store/auth.store'

import GoogleRenderButton from './GoogleRenderButton'

interface GoogleButtonProps {
  variant: 'register' | 'login'
  onSuccess?: () => void
}

const GoogleButton: RFC<GoogleButtonProps> = ({
  variant = 'login',
  onSuccess
}) => {
  const actions = useAuthStore(getAuthActions)
  const { push } = useRouter()
  const googleLogin = useGoogleLoginMutation()
  const googleRegister = useGoogleRegisterMutation()
  const { sendGA4Event } = useSendGoogleAnalyticsEvent()

  const [loading, setLoading] = useState<boolean>(false)

  const handleSuccess = useCallback(async ({ access_token }: any) => {
    try {
      setLoading(true)

      if (variant === 'register') {
        const resp = await googleRegister.mutateAsync({
          user: {
            access_token
          }
        })

        await actions.authorize(resp.jwt_token)

        sendGA4Event({ eventName: 'sign_up', method: 'google' })

        push('/sign-up/successfully-created')
      } else {
        const resp = await googleLogin.mutateAsync({
          authentication: {
            access_token,
            keep_me_logged_in: true
          }
        })

        await actions.authorize(resp.jwt_token)

        sendGA4Event({ eventName: 'login', method: 'google' })

        if (onSuccess) onSuccess()
      }
    } catch (error) {
      if (isErrorHandlerType(error)) {
        new ErrorHandler(error.data)
      }

      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [])

  return (
    <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID || ''}>
      <WebGoogleButton handleSuccess={handleSuccess} loading={loading} />
    </GoogleOAuthProvider>
  )
}

const WebGoogleButton = ({ handleSuccess, loading }) => {
  const login = useGoogleLogin({
    onSuccess: handleSuccess
  })

  return <GoogleRenderButton onClick={() => login()} loading={loading} />
}

export default GoogleButton
